import { CursorPaginatedResponseSchema, DefaultCursorPageSchema, isDefined } from 'notable-common';
import { z } from 'zod';
import { NotificationContentModality } from './content.js';
export var NotificationRequestStatus;
(function (NotificationRequestStatus) {
    NotificationRequestStatus["Pending"] = "PENDING";
    NotificationRequestStatus["Processing"] = "PROCESSING";
    NotificationRequestStatus["Failed"] = "FAILED";
    NotificationRequestStatus["Success"] = "SUCCESS";
})(NotificationRequestStatus || (NotificationRequestStatus = {}));
/** How a request chose its contact, without the contact itself. */
export var NotificationRequestRecipient;
(function (NotificationRequestRecipient) {
    NotificationRequestRecipient["ContactOnRecord"] = "ContactOnRecord";
    NotificationRequestRecipient["ExplicitContact"] = "ExplicitContact";
})(NotificationRequestRecipient || (NotificationRequestRecipient = {}));
/** No `sort`: results come back newest first. */
export const NotificationRequestListQuerySchema = z
    .object({
    ehrInformationId: z.uuid().optional().describe("Return only this patient record's requests."),
    encounterId: z.uuid().optional().describe("Return only this encounter's requests."),
    createdAfter: z.iso
        .datetime({ offset: true })
        .describe('Return only requests created at or after this instant.'),
    createdBefore: z.iso
        .datetime({ offset: true })
        .describe('Return only requests created before this instant.'),
    page: DefaultCursorPageSchema,
})
    .strict()
    .refine((query) => Date.parse(query.createdAfter) < Date.parse(query.createdBefore), {
    message: 'createdBefore must be after createdAfter',
    path: ['createdBefore'],
})
    .refine((query) => isDefined(query.ehrInformationId) || isDefined(query.encounterId), {
    message: 'ehrInformationId or encounterId is required',
});
export const NotificationRequestAttemptSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso
        .datetime()
        .describe('When the attempt last changed, usually a provider status update; no delivered time is stored.'),
    kind: z.enum(NotificationContentModality),
    source: z.string().describe('The sending feature, such as PREVISIT, FLOW_BUILDER, or BROADCAST.'),
    publisher: z.string().describe('TWILIO or SENDGRID.'),
    phoneNumberKind: z.string().nullable().describe('HOME, CELL, or WORK.'),
    success: z
        .boolean()
        .nullable()
        .describe('true: delivered (VOICE: call placed, not necessarily answered); false: failed, or an SMS with no final status after 15 minutes, which a late delivery can still turn true; null: no final provider status yet.'),
    failureReason: z
        .string()
        .nullable()
        .describe('The status it failed with, such as undelivered, bounce, no-answer, or timeout (SMS only); null when absent or withheld free text.'),
    publisherErrorCode: z
        .string()
        .nullable()
        .describe('Twilio error code for SMS or voice failures; always null for email.'),
});
export const NotificationRequestListEntrySchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    kind: z.enum(NotificationContentModality),
    status: z.enum(NotificationRequestStatus),
    undeliveredReason: z
        .string()
        .nullable()
        .describe('Why it was not sent, such as INVALID_MESSAGING_TIME; null when absent or withheld free text.'),
    recipient: z
        .enum(NotificationRequestRecipient)
        .describe("ExplicitContact: the send named its number or address, such as a flow step's, which can still be the patient's."),
    practiceId: z.uuid(),
    ehrInformationId: z.uuid().nullable(),
    encounterId: z.uuid().nullable(),
    previsitFormSetId: z.uuid().nullable(),
    notificationContentId: z.uuid().nullable(),
    notificationSchemeId: z.uuid().nullable(),
    notificationTimingId: z.uuid().nullable(),
    broadcastId: z.uuid().nullable(),
    attempts: z
        .array(NotificationRequestAttemptSchema)
        .describe('Provider sends for this request, oldest first: one per recipient and sender pair tried (a phone number and sending number, or an email address and sender); empty when nothing was sent.'),
});
export const NotificationRequestListResponseSchema = CursorPaginatedResponseSchema(NotificationRequestListEntrySchema);
//# sourceMappingURL=request.js.map